import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaRegStar, FaStar, FaStarHalfAlt, FaRuler, FaTruck, FaExchangeAlt } from 'react-icons/fa';
import LoadingSpinner from '../components/LoadingSpinner';
import WishlistButton from '../components/WishlistButton';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ProductDetail.css';

const ProductDetail = ({ onAddToCart }) => {
  const { productId } = useParams();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewMessage, setReviewMessage] = useState('');

  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      setProduct(null);
      try {
        const productResponse = await fetch(`/api/shop/products/${productId}`);
        if (!productResponse.ok) {
          setProduct(null);
          return;
        }
        const productData = await productResponse.json();
        setProduct(productData);
        setSelectedSize(productData.sizes?.[0] || '');
        setSelectedColor(productData.colors?.[0] || '');
        setQuantity(1);

        const [relatedResponse, reviewsResponse] = await Promise.all([
          fetch(`/api/shop/products/related/${productId}`),
          fetch(`/api/reviews/product/${productId}`),
        ]);
        setRelatedProducts(relatedResponse.ok ? await relatedResponse.json() : []);
        setReviews(reviewsResponse.ok ? await reviewsResponse.json() : []);
      } catch (error) {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productId]);

  const averageRating = reviews.length
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
    : 0;

  const handleAddToCart = () => {
    if (product.sizes?.length && !selectedSize) {
      setReviewMessage('Please select a size');
      return;
    }
    if (product.colors?.length && !selectedColor) {
      setReviewMessage('Please select a color');
      return;
    }
    if (product.stock < quantity) {
      setReviewMessage('Not enough stock for that quantity');
      return;
    }

    onAddToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image_url,
      image_url: product.image_url,
      category: product.category,
      size: selectedSize,
      color: selectedColor,
      quantity,
    });
    setReviewMessage('Added to your bag');
  };

  const submitReview = async event => {
    event.preventDefault();
    const token = localStorage.getItem('token');
    const response = await fetch('/api/reviews/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        product_id: product.id,
        rating: Number(reviewForm.rating),
        comment: reviewForm.comment,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      setReviewMessage(data.message || 'Could not save your review');
      return;
    }
    const reviewsResponse = await fetch(`/api/reviews/product/${product.id}`);
    setReviews(reviewsResponse.ok ? await reviewsResponse.json() : []);
    setReviewForm({ rating: 5, comment: '' });
    setReviewMessage('Thanks for the review');
    setActiveTab('reviews');
  };

  const renderStarRating = rating => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i += 1) {
      stars.push(<FaStar key={`star-${i}`} />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key='half-star' />);
    }
    while (stars.length < 5) {
      stars.push(<FaRegStar key={`empty-star-${stars.length}`} />);
    }
    return stars;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!product) {
    return (
      <div className='product-not-found'>
        <h2>Product Not Found</h2>
        <p>Sorry, the product you are looking for does not exist.</p>
        <Link to='/shop' className='back-to-shop'>
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <div className='product-detail-page'>
      <div className='product-detail-container'>
        <div className='product-gallery'>
          <div className='product-main-image'>
            <img src={product.image_url} alt={product.name} />
          </div>
        </div>

        <div className='product-info'>
          <h1 className='product-name'>{product.name}</h1>

          <div className='product-meta'>
            <div className='product-rating'>
              {renderStarRating(averageRating)}
              <span className='rating-count'>
                ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
              </span>
            </div>
            <div className='product-category'>
              <span>Category: </span>
              <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
                {product.category}
              </Link>
            </div>
          </div>

          <div className='product-price'>${Number(product.price).toFixed(2)}</div>
          <p>{product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}</p>

          <div className='product-description'>
            <p>{product.description}</p>
          </div>

          <div className='product-options'>
            {product.sizes?.length > 0 && (
              <div className='size-options'>
                <div className='option-header'>
                  <h3>Size</h3>
                  <button className='size-guide-btn' onClick={() => setShowSizeGuide(!showSizeGuide)}>
                    <FaRuler /> Size Guide
                  </button>
                </div>
                {showSizeGuide && (
                  <div className='size-guide-modal'>
                    <div className='size-guide-content'>
                      <button className='close-btn' onClick={() => setShowSizeGuide(false)}>
                        ×
                      </button>
                      <h3>Size Guide</h3>
                      <table className='size-chart'>
                        <thead>
                          <tr>
                            <th>Size</th>
                            <th>Bust (in)</th>
                            <th>Waist (in)</th>
                            <th>Hips (in)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>XS</td>
                            <td>31-32</td>
                            <td>24-25</td>
                            <td>34-35</td>
                          </tr>
                          <tr>
                            <td>S</td>
                            <td>33-34</td>
                            <td>26-27</td>
                            <td>36-37</td>
                          </tr>
                          <tr>
                            <td>M</td>
                            <td>35-36</td>
                            <td>28-29</td>
                            <td>38-39</td>
                          </tr>
                          <tr>
                            <td>L</td>
                            <td>37-38</td>
                            <td>30-31</td>
                            <td>40-41</td>
                          </tr>
                          <tr>
                            <td>XL</td>
                            <td>39-40</td>
                            <td>32-33</td>
                            <td>42-43</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className='size-buttons'>
                  {product.sizes.map(size => (
                    <button
                      key={size}
                      className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.colors?.length > 0 && (
              <div className='color-options'>
                <h3>Color: {selectedColor}</h3>
                <div className='color-buttons'>
                  {product.colors.map(color => (
                    <button
                      key={color}
                      className={`color-btn ${selectedColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color.toLowerCase() }}
                      onClick={() => setSelectedColor(color)}
                      title={color}
                    >
                      <span className='sr-only'>{color}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className='quantity-selector'>
              <h3>Quantity</h3>
              <div className='quantity-input'>
                <button
                  className='quantity-btn'
                  disabled={quantity <= 1}
                  onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                >
                  -
                </button>
                <input
                  type='number'
                  min='1'
                  max={product.stock || 1}
                  value={quantity}
                  onChange={event =>
                    setQuantity(Math.max(1, Math.min(product.stock || 1, parseInt(event.target.value, 10) || 1)))
                  }
                />
                <button
                  className='quantity-btn'
                  disabled={quantity >= (product.stock || 1)}
                  onClick={() => setQuantity(prev => Math.min(product.stock || 1, prev + 1))}
                >
                  +
                </button>
              </div>
            </div>

            <div className='product-actions'>
              <button className='add-to-cart-btn' onClick={handleAddToCart} disabled={product.stock < 1}>
                {product.stock < 1 ? 'Out of Stock' : 'Add to Cart'}
              </button>
              <WishlistButton productId={product.id} />
            </div>
            {reviewMessage && <p>{reviewMessage}</p>}
          </div>

          <div className='product-features'>
            <div className='feature'>
              <FaTruck /> <span>Free shipping on orders over $100</span>
            </div>
            <div className='feature'>
              <FaExchangeAlt /> <span>Easy 30-day returns</span>
            </div>
          </div>
        </div>
      </div>

      <div className='product-additional-info'>
        <div className='info-tabs'>
          <button
            className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews
          </button>
          <button
            className={`tab-btn ${activeTab === 'shipping' ? 'active' : ''}`}
            onClick={() => setActiveTab('shipping')}
          >
            Shipping & Returns
          </button>
        </div>

        <div className='tab-content'>
          {activeTab === 'details' && (
            <div className='product-details'>
              <h3>Product Details</h3>
              <p>{product.description}</p>
            </div>
          )}
          {activeTab === 'reviews' && (
            <div className='product-details'>
              <h3>Reviews</h3>
              {reviews.length === 0 && <p>No reviews yet.</p>}
              {reviews.map(review => (
                <article key={review.id}>
                  <strong>{review.user}</strong> · {review.rating}/5
                  <p>{review.comment}</p>
                </article>
              ))}
              {isAuthenticated ? (
                <form onSubmit={submitReview}>
                  <label htmlFor='rating'>Rating</label>
                  <select
                    id='rating'
                    value={reviewForm.rating}
                    onChange={event => setReviewForm(current => ({ ...current, rating: event.target.value }))}
                  >
                    {[5, 4, 3, 2, 1].map(value => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <textarea
                    value={reviewForm.comment}
                    onChange={event => setReviewForm(current => ({ ...current, comment: event.target.value }))}
                    placeholder='How does it fit and feel?'
                    required
                  />
                  <button type='submit'>Submit review</button>
                </form>
              ) : (
                <p>
                  <Link to='/auth?mode=login'>Log in</Link> to write a review.
                </p>
              )}
            </div>
          )}
          {activeTab === 'shipping' && (
            <div className='product-details'>
              <h3>Shipping & Returns</h3>
              <p>Standard shipping is $5.99 and free on orders over $100. Returns are accepted within 30 days.</p>
            </div>
          )}
        </div>
      </div>

      {relatedProducts.length > 0 && (
        <div className='related-products'>
          <h2>You May Also Like</h2>
          <div className='related-products-grid'>
            {relatedProducts.map(relatedProduct => (
              <div key={relatedProduct.id} className='related-product-card'>
                <Link to={`/product/${relatedProduct.id}`} className='product-image'>
                  <img src={relatedProduct.image_url} alt={relatedProduct.name} />
                </Link>
                <div className='product-details'>
                  <h3>
                    <Link to={`/product/${relatedProduct.id}`}>{relatedProduct.name}</Link>
                  </h3>
                  <p className='product-price'>${Number(relatedProduct.price).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
